import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

/**
 * Custom hook to get task badge states for all incidents in the organization
 * Returns a map of incidentId -> badge state object { totalCount, overdueCount, state }
 * States: 'normal' (blue), 'critical' (red, overdue tasks present)
 */
export const useIncidentOverdueTasks = () => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [taskBadgeStates, setTaskBadgeStates] = useState(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.orgId || !currentUser?.role || !db) {
      setTaskBadgeStates(new Map());
      setLoading(false);
      return;
    }

    console.log('🔍 useIncidentOverdueTasks: Setting up listener for org:', currentUser.orgId, 'role:', currentUser.role);

    const tasksRef = collection(db, 'c3_tasks');
    const q = query(
      tasksRef,
      where('orgId', '==', currentUser.orgId)
      // Removed status filter to avoid potential index issues - will filter manually
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allTasks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log('🔍 useIncidentOverdueTasks: Processing tasks:', allTasks.length, 'for role:', currentUser.role);
      
      // Filter tasks assigned to current user's role and not completed
      const userTasks = allTasks.filter(task => 
        task.assignedRole === currentUser.role && 
        task.status !== 'complete' &&
        task.incidentId
      );

      console.log('🔍 useIncidentOverdueTasks: User role tasks:', userTasks.length);

      // Helper function for task state calculation
      const now = new Date();

      const isTaskOverdue = (task) => {
        if (!task.dueDate) return false;
        const dueDate = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
        return dueDate < now;
      };

      // Group tasks by incident and calculate badge states
      const badgeStatesMap = new Map();
      
      userTasks.forEach(task => {
        const incidentId = task.incidentId;
        
        if (!badgeStatesMap.has(incidentId)) {
          badgeStatesMap.set(incidentId, {
            totalCount: 0,
            overdueCount: 0,
            state: 'normal'
          });
        }
        
        const badgeState = badgeStatesMap.get(incidentId);
        badgeState.totalCount++;
        
        if (isTaskOverdue(task)) {
          badgeState.overdueCount++;
          console.log('🔍 useIncidentOverdueTasks: Found overdue task:', task.name, 'for incident:', incidentId);
        }
      });

      // Calculate final badge state for each incident
      const finalStatesMap = new Map();
      badgeStatesMap.forEach((badgeState, incidentId) => {
        let finalState = 'normal';
        
        if (badgeState.overdueCount > 0) {
          finalState = 'critical';
        }
        
        finalStatesMap.set(incidentId, {
          totalCount: badgeState.totalCount,
          overdueCount: badgeState.overdueCount,
          state: finalState
        });
      });

      console.log('🔍 useIncidentOverdueTasks: Final badge states:', Object.fromEntries(finalStatesMap));
      setTaskBadgeStates(finalStatesMap);
      setLoading(false);
    }, (error) => {
      console.error('❌ Error fetching task badge states:', error);
      setTaskBadgeStates(new Map());
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.orgId, currentUser?.role, db]);

  return { taskBadgeStates, loading };
}; 